import { isSettingLocked, setSettingLock } from "../../../erd-editor-schema/dist/v3/schema/settings.js";
import { replaceOperator } from "../../../erd-editor-schema/dist/query/lww.js";
import { CanvasType, DDLScriptPositionList, LockSettingFields, LockSettingType, LockSettingTypeList, Show } from "../../../constants/schema.js";
import { hasBracketType, hasCanvasType, hasColumnType, hasDatabase, hasLanguage, hasNameCase, hasNameViewMode, maxWidthCommentInRange, zoomInRange, zoomLevelInRange } from "../../../utils/validation.js";
import { createAction } from "../../../r-html/dist/store/index.js";
import { Tag } from "../../tag.js";
import { bHas } from "../../../utils/bit.js";
import { ActionType, CodeSettingLocks } from "./actions.js";
import { endScreenBaseline, isScreenWrite, keepScreenBaseline, landScreenBaseline, settingWriteTarget, viewportWriteTarget } from "./screenBaseline.js";
import { VIEWPORT_REGISTER, ViewportRegister, isOwnStep } from "../../geometryRegisters.js";
import { keepFocusShown } from "../editor/utils/nameView.js";
import { releaseMemos, releaseTableGroups } from "../editor/utils/releaseHidden.js";
import { viewScrollToAction, viewStreamScrollToAction } from "../editor/view.actions.js";
import { getSceneTransform } from "../../../konva/scene/viewport.js";
import { getFrozenOrigin, getViewContentRect } from "../../../konva/scene/viewFreeze.js";
import { contentScrollRange, openingOrigin } from "./scrollRange.js";
import { clamp, isNil, isNumber, isString, pick } from "es-toolkit";
import { round } from "es-toolkit/compat";
//#region src/engine/modules/settings/atom.actions.ts
var changeDatabaseNameAction = createAction(ActionType.changeDatabaseName);
var changeDatabaseName = ({ settings, lww }, { payload: { value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	replaceOperator(lww, safeVersion, "settings.databaseName", "settings", "databaseName", () => {
		settings.databaseName = value;
	});
};
function replaceViewport(state, version, path, tags, write) {
	if (isScreenWrite(tags)) return write();
	replaceOperator(state.lww, version, VIEWPORT_REGISTER, "settings", path, write);
}
function stepViewport(state, context, version, path, tags, write) {
	if (!isOwnStep(context, tags)) return write();
	replaceViewport(state, version ?? context.clock.getVersion(), path, tags, write);
}
var changeZoomLevelAction = createAction(ActionType.changeZoomLevel);
var changeZoomLevel = (state, { payload: { value }, version, tags }, { clock }) => {
	if (!isNil(tags) && bHas(tags, Tag.following)) return;
	const target = viewportWriteTarget(state, tags);
	replaceViewport(state, version ?? clock.getVersion(), ViewportRegister.zoomLevel, tags, () => {
		target.zoomLevel = zoomLevelInRange(value);
	});
};
var streamZoomLevelAction = createAction(ActionType.streamZoomLevel);
var streamZoomLevel = (state, { payload: { value }, version, tags }, context) => {
	if (!isNil(tags) && bHas(tags, Tag.following)) return;
	const target = viewportWriteTarget(state, tags);
	stepViewport(state, context, version, ViewportRegister.zoomLevel, tags, () => {
		target.zoomLevel = zoomLevelInRange(target.zoomLevel + value);
	});
};
var still = (origin) => ({
	min: origin,
	max: origin
});
var hasViewport = ({ width, height }) => width > 0 && height > 0;
function getContentScrollRanges(state, source = "document") {
	const { originX, originY, zoomLevel } = getSceneTransform(state, source);
	const { viewport } = state.editor;
	const content = getViewContentRect(state, source);
	if (!content || !hasViewport(viewport)) return {
		left: still(originX),
		top: still(originY)
	};
	return {
		left: contentScrollRange(content.x, content.x + content.width, viewport.width, zoomLevel),
		top: contentScrollRange(content.y, content.y + content.height, viewport.height, zoomLevel)
	};
}
function getOpeningOrigin(state) {
	const { settings: { originX, originY, zoomLevel }, editor: { viewport } } = state;
	const content = getViewContentRect(state);
	if (!content || !hasViewport(viewport)) return {
		x: originX,
		y: originY
	};
	return {
		x: openingOrigin(originX, content.x, content.x + content.width, viewport.width, zoomLevel),
		y: openingOrigin(originY, content.y, content.y + content.height, viewport.height, zoomLevel)
	};
}
var hull = (range, ...origins) => ({
	min: Math.min(range.min, ...origins),
	max: Math.max(range.max, ...origins)
});
function getScrollRanges(state, source = "document") {
	const { originX, originY } = getSceneTransform(state, source);
	const { left, top } = getContentScrollRanges(state, source);
	const anchor = getFrozenOrigin(state, source);
	return anchor ? {
		left: hull(left, originX, anchor.x),
		top: hull(top, originY, anchor.y)
	} : {
		left: hull(left, originX),
		top: hull(top, originY)
	};
}
function clampScrollMovement(state, { movementX, movementY }, source = "document") {
	if (!hasViewport(state.editor.viewport)) return {
		movementX,
		movementY
	};
	const { originX, originY } = getSceneTransform(state, source);
	const { left, top } = getScrollRanges(state, source);
	return {
		movementX: clamp(originX + movementX, left.min, left.max) - originX,
		movementY: clamp(originY + movementY, top.min, top.max) - originY
	};
}
var scrollToAction = createAction(ActionType.scrollTo);
var scrollTo = (state, { payload: { originX, originY }, version, tags }, { clock }) => {
	if (!isNil(tags) && bHas(tags, Tag.following)) return;
	if (isScreenWrite(tags)) {
		state.editor.scrollPullPending = false;
		state.editor.originShown = true;
	}
	const target = viewportWriteTarget(state, tags);
	replaceViewport(state, version ?? clock.getVersion(), ViewportRegister.origin, tags, () => {
		target.originX = round(originX, 4);
		target.originY = round(originY, 4);
	});
};
var streamScrollToAction = createAction(ActionType.streamScrollTo);
var streamScrollTo = (state, { payload: { movementX, movementY }, version, tags }, context) => {
	if (!isNil(tags) && bHas(tags, Tag.following)) return;
	const target = viewportWriteTarget(state, tags);
	stepViewport(state, context, version, ViewportRegister.origin, tags, () => {
		target.originX = round(target.originX + movementX, 4);
		target.originY = round(target.originY + movementY, 4);
	});
};
var sceneScrollToAction = (source, payload) => source === "document" ? scrollToAction(payload) : viewScrollToAction({
	...payload,
	kind: source
});
var sceneStreamScrollToAction = (source, payload) => source === "document" ? streamScrollToAction(payload) : viewStreamScrollToAction({
	...payload,
	kind: source
});
var changeShowAction = createAction(ActionType.changeShow);
var changeShow = (state, { payload: { show, value }, tags }) => {
	const target = settingWriteTarget(state, tags, "show");
	const bits = target.show ?? 0;
	target.show = value ? bits | show : bits & ~show;
	if (target !== state.settings) return;
	if (value && bHas(show, Show.hideTableGroup)) releaseTableGroups(state);
	if (value && bHas(show, Show.hideMemo)) releaseMemos(state);
	if (bHas(show, Show.columnDomain)) keepFocusShown(state);
};
var changeDatabaseAction = createAction(ActionType.changeDatabase);
var changeDatabase = (state, { payload: { value }, tags }) => {
	if (hasDatabase(value)) settingWriteTarget(state, tags, "database").database = value;
};
function rememberCanvasType({ settings, editor }) {
	if (settings.canvasType !== CanvasType.settings) editor.lastCanvasType = settings.canvasType;
}
var changeCanvasTypeAction = createAction(ActionType.changeCanvasType);
var changeCanvasType = (state, { payload: { value }, tags }) => {
	if (!isNil(tags) && bHas(tags, Tag.following)) return;
	const target = settingWriteTarget(state, tags, "canvasType");
	if (target !== state.settings) {
		target.canvasType = value;
		return;
	}
	rememberCanvasType(state);
	state.settings.canvasType = value;
	if (value !== CanvasType.ERD) state.editor.drawTableGroup = false;
};
function replaceCodeSetting(state, version, field, value, tags) {
	const target = settingWriteTarget(state, tags, field);
	const write = () => {
		target[field] = value;
	};
	if (isScreenWrite(tags)) return write();
	replaceOperator(state.lww, version, "settings.code", "settings", field, write);
}
function setCodeSetting(state, context, field, { value, shared }, version, tags) {
	if (shared === true || isScreenWrite(tags) || !isSettingLocked(state.settings.lockSettings, CodeSettingLocks[field])) {
		replaceCodeSetting(state, version ?? context.clock.getVersion(), field, value, tags);
		return;
	}
	if (!isOwnStep(context, tags)) return;
	settingWriteTarget(state, tags, field)[field] = value;
}
var changeLanguageAction = createAction(ActionType.changeLanguage);
var changeLanguage = (state, { payload, version, tags }, context) => {
	if (hasLanguage(payload.value)) setCodeSetting(state, context, "language", payload, version, tags);
};
var changeTableNameCaseAction = createAction(ActionType.changeTableNameCase);
var changeTableNameCase = (state, { payload, version, tags }, context) => {
	if (hasNameCase(payload.value)) setCodeSetting(state, context, "tableNameCase", payload, version, tags);
};
var changeColumnNameCaseAction = createAction(ActionType.changeColumnNameCase);
var changeColumnNameCase = (state, { payload, version, tags }, context) => {
	if (hasNameCase(payload.value)) setCodeSetting(state, context, "columnNameCase", payload, version, tags);
};
var changeBracketTypeAction = createAction(ActionType.changeBracketType);
var changeBracketType = (state, { payload, version, tags }, context) => {
	if (hasBracketType(payload.value)) setCodeSetting(state, context, "bracketType", payload, version, tags);
};
var changeRelationshipDataTypeSyncAction = createAction(ActionType.changeRelationshipDataTypeSync);
var changeRelationshipDataTypeSync = ({ settings }, { payload: { value } }) => {
	settings.relationshipDataTypeSync = value;
};
var changeColumnOrderAction = createAction(ActionType.changeColumnOrder);
var changeColumnOrder = ({ settings }, { payload: { value, target } }) => {
	if (value === target || !hasColumnType(value) || !hasColumnType(target)) return;
	const index = settings.columnOrder.indexOf(value);
	const targetIndex = settings.columnOrder.indexOf(target);
	if (index === -1 || targetIndex === -1) return;
	settings.columnOrder.splice(index, 1);
	settings.columnOrder.splice(targetIndex, 0, value);
};
var changeMaxWidthCommentAction = createAction(ActionType.changeMaxWidthComment);
var changeMaxWidthComment = ({ settings }, { payload: { value } }) => {
	settings.maxWidthComment = value === -1 ? value : maxWidthCommentInRange(value);
};
var changeLockSettingsAction = createAction(ActionType.changeLockSettings);
var isLockedValue = {
	originX: Number.isFinite,
	originY: Number.isFinite,
	zoomLevel: (value) => isNumber(value) && zoomInRange(value) === value,
	canvasType: (value) => hasCanvasType(value) && value !== CanvasType.settings,
	language: hasLanguage,
	tableNameCase: hasNameCase,
	columnNameCase: hasNameCase,
	bracketType: hasBracketType,
	nameViewMode: hasNameViewMode
};
var CodeLockSettings = Object.values(CodeSettingLocks).reduce((acc, bit) => acc | bit, 0);
var UnlockCarriedSettings = CodeLockSettings | LockSettingType.unlockNameViewMode;
var changeLockSettings = (state, { payload: { lockSettingType, value, values }, version, tags }, { clock }) => {
	const { settings, lww } = state;
	const safeVersion = version ?? clock.getVersion();
	LockSettingTypeList.forEach((bit) => {
		if (!bHas(lockSettingType, bit)) return;
		const fields = LockSettingFields[bit];
		const carried = pick(values, fields);
		const valid = fields.filter((field) => isLockedValue[field](carried[field]));
		if (value && valid.length < fields.length) return;
		replaceOperator(lww, safeVersion, "settings.lockSettings", "settings", String(bit), () => {
			if (value) Object.assign(settings.lockedValues, carried);
			settings.lockSettings = setSettingLock(settings.lockSettings, bit, value);
		});
		if (value || !bHas(UnlockCarriedSettings, bit)) return;
		valid.forEach((field) => field === "nameViewMode" ? replaceNameViewMode(state, safeVersion, carried.nameViewMode) : replaceCodeSetting(state, safeVersion, field, carried[field], tags));
	});
};
var changeIgnoreSaveSettingsAction = createAction(ActionType.changeIgnoreSaveSettings);
var changeIgnoreSaveSettings = () => {};
var changeDDLScriptAction = createAction(ActionType.changeDDLScript);
var changeDDLScript = ({ settings, lww }, { payload: { position, value }, version }, { clock }) => {
	if (!DDLScriptPositionList.includes(position) || !isString(value)) return;
	const safeVersion = version ?? clock.getVersion();
	replaceOperator(lww, safeVersion, "settings.ddlScripts", "settings", position, () => {
		settings.ddlScripts = {
			...settings.ddlScripts,
			[position]: value
		};
	});
};
function showNameViewMode(state, value) {
	state.settings.nameViewMode = value;
	keepFocusShown(state);
}
function replaceNameViewMode(state, version, value) {
	replaceOperator(state.lww, version, "settings.nameViewMode", "settings", "nameViewMode", () => {
		endScreenBaseline(state.editor, "nameViewMode");
		showNameViewMode(state, value);
	});
}
var changeNameViewModeAction = createAction(ActionType.changeNameViewMode);
var changeNameViewMode = (state, { payload: { value, shared, editorId }, version, tags }, { clock }) => {
	if (!hasNameViewMode(value)) return;
	const screen = isScreenWrite(tags);
	if (shared === true && !screen) {
		replaceNameViewMode(state, version ?? clock.getVersion(), value);
		return;
	}
	if (editorId !== state.editor.id) return;
	if (screen) keepScreenBaseline(state, "nameViewMode");
	else endScreenBaseline(state.editor, "nameViewMode");
	showNameViewMode(state, value);
};
var toNameViewModeAction = (state, value, readonly) => changeNameViewModeAction({
	value,
	shared: !readonly && !isSettingLocked(state.settings.lockSettings, LockSettingType.unlockNameViewMode),
	editorId: state.editor.id
});
var LOCK_KNOWN = LockSettingTypeList.reduce((acc, bit) => acc | bit, 0);
function landLoadedSettings(state, loaded, version, tags) {
	const { settings, lww } = state;
	const { lockSettings, lockedValues, ...screen } = settings;
	Object.assign(settings, loaded, {
		lockSettings: lockSettings & LOCK_KNOWN | loaded.lockSettings & ~LOCK_KNOWN,
		lockedValues: { ...lockedValues }
	});
	LockSettingTypeList.forEach((bit) => {
		const fields = LockSettingFields[bit];
		replaceOperator(lww, version, "settings.lockSettings", "settings", String(bit), () => {
			settings.lockSettings = bHas(loaded.lockSettings, bit) ? settings.lockSettings | bit : settings.lockSettings & ~bit;
			Object.assign(settings.lockedValues, pick(loaded.lockedValues, fields));
		});
		if (bit !== LockSettingType.viewport && isSettingLocked(settings.lockSettings, bit)) Object.assign(settings, pick(screen, fields));
	});
	landScreenBaseline(state, screen, tags);
}
var settingsReducers = {
	[ActionType.changeDatabaseName]: changeDatabaseName,
	[ActionType.changeZoomLevel]: changeZoomLevel,
	[ActionType.streamZoomLevel]: streamZoomLevel,
	[ActionType.scrollTo]: scrollTo,
	[ActionType.streamScrollTo]: streamScrollTo,
	[ActionType.changeShow]: changeShow,
	[ActionType.changeDatabase]: changeDatabase,
	[ActionType.changeCanvasType]: changeCanvasType,
	[ActionType.changeLanguage]: changeLanguage,
	[ActionType.changeTableNameCase]: changeTableNameCase,
	[ActionType.changeColumnNameCase]: changeColumnNameCase,
	[ActionType.changeBracketType]: changeBracketType,
	[ActionType.changeRelationshipDataTypeSync]: changeRelationshipDataTypeSync,
	[ActionType.changeColumnOrder]: changeColumnOrder,
	[ActionType.changeMaxWidthComment]: changeMaxWidthComment,
	[ActionType.changeLockSettings]: changeLockSettings,
	[ActionType.changeIgnoreSaveSettings]: changeIgnoreSaveSettings,
	[ActionType.changeDDLScript]: changeDDLScript,
	[ActionType.changeNameViewMode]: changeNameViewMode
};
var actions = {
	changeDatabaseNameAction,
	changeZoomLevelAction,
	streamZoomLevelAction,
	scrollToAction,
	streamScrollToAction,
	changeShowAction,
	changeDatabaseAction,
	changeCanvasTypeAction,
	changeLanguageAction,
	changeTableNameCaseAction,
	changeColumnNameCaseAction,
	changeBracketTypeAction,
	changeRelationshipDataTypeSyncAction,
	changeColumnOrderAction,
	changeMaxWidthCommentAction,
	changeLockSettingsAction,
	changeIgnoreSaveSettingsAction,
	changeDDLScriptAction,
	changeNameViewModeAction
};
//#endregion
export { CodeLockSettings, UnlockCarriedSettings, actions, changeBracketTypeAction, changeCanvasTypeAction, changeColumnNameCaseAction, changeColumnOrderAction, changeDDLScriptAction, changeDatabaseAction, changeDatabaseNameAction, changeIgnoreSaveSettingsAction, changeLanguageAction, changeLockSettingsAction, changeMaxWidthCommentAction, changeNameViewModeAction, changeRelationshipDataTypeSyncAction, changeShowAction, changeTableNameCaseAction, changeZoomLevelAction, clampScrollMovement, getContentScrollRanges, getOpeningOrigin, getScrollRanges, hasViewport, landLoadedSettings, rememberCanvasType, sceneScrollToAction, sceneStreamScrollToAction, scrollToAction, settingsReducers, streamScrollToAction, streamZoomLevelAction, toNameViewModeAction };
